import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { Capture } from '../../../core/models/capture.model';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { CaptureStatus } from '../../../core/enums/capture-status.enum';
import { CaptureService } from 'app/core/services/capture.service';
import { UserRoleName } from 'app/core/enums/user-role-name.enum';
import { NgClass, DatePipe } from '@angular/common';
import { RouterLinkActive, RouterLink } from '@angular/router';
import { UserPresenceComponent } from '../../../shared/components/user-presence/user-presence.component';
import { CaptureSnapshotComponent } from '../capture-snapshot/capture-snapshot.component';
import { CaptureReprocessComponent } from '../capture-reprocess/capture-reprocess.component';
import { CaptureUnverifyComponent } from '../capture-unverify/capture-unverify.component';
import { ClientNameComponent } from '../../../shared/components/client-name/client-name.component';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { CandidatePatientNameComponent } from '../candidate-patient-name/candidate-patient-name.component';
import { ExpirationDateComponent } from '../../../shared/components/expiration-date/expiration-date.component';
import { HighValueIndicatorComponent } from '../../../shared/components/high-value-indicator/high-value-indicator.component';
import { CandidateDrugComponent } from '../candidate-drug/candidate-drug.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { PrescriberOfficesComponent } from '../../../shared/components/prescriber-offices/prescriber-offices.component';
import { CaptureStatusPipe } from 'app/shared/pipes/capture-status.pipe';
import { CaptureStatusReasonPipe } from '../../../shared/pipes/capture-status-reason.pipe';
import { JoinListPipe } from '../../../shared/pipes/join-list.pipe';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { ModelEventsService, ModelEventsSubscriptionSet, CaptureRemoteEvent, CaptureRemoteEventType } from 'app/core/services/model-events.service';
import { AppToastService } from 'app/core/services/app-toast.service';
import { showCaptureTransitionedToast, showCaptureStatusReasonUpdatedToast } from 'app/core/lib/verification-utils';
import { SpecialtyBadgesComponent } from '../../../shared/components/specialty-badges/specialty-badges.component';

@Component({
  selector: 'app-capture-header',
  templateUrl: './capture-header.component.html',
  styleUrls: ['./capture-header.component.scss'],
  imports: [NgClass,
    RouterLinkActive,
    RouterLink,
    UserPresenceComponent,
    CaptureSnapshotComponent,
    CaptureReprocessComponent,
    CaptureUnverifyComponent,
    ClientNameComponent,
    PatientNameComponent,
    CandidatePatientNameComponent,
    ExpirationDateComponent,
    HighValueIndicatorComponent,
    CandidateDrugComponent,
    PrescriberNameComponent,
    PrescriberOfficesComponent,
    DatePipe,
    CaptureStatusPipe,
    CaptureStatusReasonPipe,
    JoinListPipe,
    CapitalizePipe,
    SpecialtyBadgesComponent]
})
export class CaptureHeaderComponent implements OnInit, OnDestroy {
  private authService = inject(AuthenticationService);
  private captureService = inject(CaptureService);
  private modelEventsService = inject(ModelEventsService);
  private toastService = inject(AppToastService);

  private _capture: Capture;
  private captureChangedSubscription = null;

  canSnapshot = false;
  canReprocess = false;
  canViewHistory = false;
  canUnverify = false;
  canViewStatusDetailReport = false;
  canViewFax = false;
  canViewPatient = false;
  canViewClient = false;
  showEstimatedValue = false;
  showClientReviewTask = false;
  isHidden = false;
  remoteEventsSubscription: Subscription;
  remoteEventsSubscriptionSet: ModelEventsSubscriptionSet<CaptureRemoteEvent>;

  @Input()
  set capture(capture: Capture) {
    this._capture = capture;
    this.refreshCaptureViewState();
  }

  get capture(): Capture {
    return this._capture;
  }

  private refreshCaptureViewState() {
    const capture = this._capture;
    this.isHidden = capture.status === CaptureStatus.needsPatientMatchApproval && this.authService.isCaptureAdminUser;
    this.showClientReviewTask = this.authService.isCaptureAdminUser;
    this.canUnverify = this.authService.isCaptureAdminUser &&
      this.authService.hasPermissionTo(UserRoleName.editVerifiedCaptures) &&
      capture.unverifiable;
    this.canViewClient = this.authService.isCaptureAdminUser;
  }

  ngOnInit() {
    this.canSnapshot = this.authService.isCaptureAdminUser;
    this.canViewHistory = this.authService.isCaptureAdminUser;
    this.canViewFax = this.authService.isCaptureAdminUser;
    this.showEstimatedValue = this.authService.isCaptureAdminUser;
    this.canReprocess = this.authService.isCaptureAdminUser;
    this.canViewStatusDetailReport = this.authService.isCaptureAdminUser &&
      this.authService.hasPermissionTo(UserRoleName.manageProduct);
    this.canViewPatient = this.authService.isCaptureAdminUser;

    this.captureChangedSubscription = this.captureService.captureChanged.subscribe(({ capture }) => {
      if (capture.id === this.capture.id) {
        this.capture = capture;
      }
    });
    this.setupRemoteEventsSubscriptionSet();
  }

  ngOnDestroy() {
    this.remoteEventsSubscriptionSet.unsubscribeAll();
    this.remoteEventsSubscription?.unsubscribe()
    this.captureChangedSubscription?.unsubscribe();
  }

  public get showHealthGorillaTag(): boolean {
    const clientEnabled = this.capture.client.healthGorillaSettings?.enabled;
    const userAllowed = this.authService.hasPermissionTo(UserRoleName.accessHieData);
    const patientHasData = !!this.capture.patient.healthGorillaIdentifier;

    return clientEnabled && userAllowed && patientHasData
  }

  private setupRemoteEventsSubscriptionSet() {
    this.remoteEventsSubscriptionSet = this.modelEventsService.newSet<CaptureRemoteEvent>();
    this.remoteEventsSubscription =
      this.remoteEventsSubscriptionSet.events.subscribe(([_modelName, _modelId, event]) => {
        this.capture.status = event.data.status;
        this.capture.statusReason = event.data.statusReason;
        this.capture.unverifiable = event.data.unverifiable;
        this.refreshCaptureViewState();
        if (event.type === CaptureRemoteEventType.statusUpdate) {
          showCaptureTransitionedToast(this.toastService, event.data.status);
        } else if (event.type === CaptureRemoteEventType.statusReasonUpdate) {
          showCaptureStatusReasonUpdatedToast(this.toastService, event.data.statusReason);
        }
      });

    this.remoteEventsSubscriptionSet.subscribe('Capture', this.capture.id);
  }

  onShowClick() {
    this.isHidden = false;
  }
}
